import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Scale,
  Assessment,
  Grade,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface CarcassReportProps {
  lotId?: string;
}

export const CarcassReport: React.FC<CarcassReportProps> = ({ lotId = '542' }) => {
  // Mock data for slaughterhouse operations
  const carcassData = {
    lotId,
    receiptDate: '28/11/2024',
    totalAnimals: 45,
    processedAnimals: 42,
    averageWeight: '485 kg',
    totalYield: '18.5 toneladas',
    yieldPercentage: 78.2,
    qualityGrade: 'A',
    inspector: 'Dr. Carlos Mendes',
    status: 'Processamento Concluído',
    yields: [
      { cut: 'Dianteiro', weight: '4.2 ton', percentage: 22.7 },
      { cut: 'Traseiro', weight: '6.8 ton', percentage: 36.8 },
      { cut: 'Costela', weight: '2.1 ton', percentage: 11.4 },
      { cut: 'Miúdos', weight: '1.8 ton', percentage: 9.7 },
      { cut: 'Outros', weight: '3.6 ton', percentage: 19.4 },
    ],
    qualityMetrics: [
      { metric: 'pH Carne', value: '5.8', status: 'normal', target: '5.4-6.0' },
      { metric: 'Temperatura', value: '2°C', status: 'normal', target: '0-4°C' },
      { metric: 'Marmorização', value: '7/10', status: 'good', target: '6-10' },
      { metric: 'Cor', value: 'Vermelho cereja', status: 'normal', target: 'Padrão' },
    ],
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return DESIGN_TOKENS.colors.status.success[500];
      case 'good': return DESIGN_TOKENS.colors.status.info[500];
      case 'warning': return DESIGN_TOKENS.colors.status.warning[500];
      default: return DESIGN_TOKENS.colors.text.secondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'normal': return <CheckCircle />;
      case 'good': return <Grade />;
      case 'warning': return <Warning />;
      default: return <Assessment />;
    }
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: DESIGN_TOKENS.colors.organization.slaughterhouse,
            mx: 'auto',
            mb: 2,
            fontSize: '2rem',
          }}
        >
          <Scale sx={{ fontSize: '2.5rem' }} />
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.slaughterhouse }}>
          Relatório de Carcaça
        </Typography>
        <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
          Lote #{carcassData.lotId}
        </Typography>
        <Chip 
          label={carcassData.status} 
          color="success" 
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Processing Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.slaughterhouse }}>
            Resumo do Processamento
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Assessment sx={{ color: DESIGN_TOKENS.colors.organization.slaughterhouse }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Total de Animais</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{carcassData.totalAnimals}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircle sx={{ color: DESIGN_TOKENS.colors.organization.slaughterhouse }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Processados</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{carcassData.processedAnimals}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Scale sx={{ color: DESIGN_TOKENS.colors.organization.slaughterhouse }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Peso Médio</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{carcassData.averageWeight}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Grade sx={{ color: DESIGN_TOKENS.colors.organization.slaughterhouse }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Rendimento Total</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{carcassData.totalYield}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Yield Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.slaughterhouse }}>
            Distribuição de Cortes
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Rendimento Total: {carcassData.yieldPercentage}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={carcassData.yieldPercentage} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: DESIGN_TOKENS.colors.neutral[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: DESIGN_TOKENS.colors.organization.slaughterhouse,
                }
              }} 
            />
          </Box>
          {carcassData.yields.map((cut, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {cut.cut}
                </Typography>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  {cut.weight}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.slaughterhouse }}>
                {cut.percentage}%
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Quality Metrics */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.slaughterhouse }}>
            Métricas de Qualidade
          </Typography>
          {carcassData.qualityMetrics.map((metric, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              py: 2,
              borderBottom: index < carcassData.qualityMetrics.length - 1 ? `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` : 'none'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: getStatusColor(metric.status) }}>
                  {getStatusIcon(metric.status)}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {metric.metric}
                  </Typography>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    Alvo: {metric.target}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ 
                fontWeight: 600, 
                color: getStatusColor(metric.status) 
              }}>
                {metric.value}
              </Typography>
            </Box>
          ))}
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
            <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              Inspetor responsável: {carcassData.inspector}
            </Typography>
            <br />
            <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              Data de recebimento: {carcassData.receiptDate}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};